import { useState } from 'react'
import { ArrowRight, CheckCircle, X } from '@/ui/icons'
import { RangerMark } from '@/modules/ai'
import { Button, Card, Textarea } from '@/ui/design-system'
import { useAddMealsWithRanger, addMealsErrorMessage } from '@/modules/meals/hooks/useAddMealsWithRanger'
import { useTripChecklist } from '@/modules/trips/hooks/useTripChecklist'
import type { ChecklistItemId, TripChecklistTrip } from '@/modules/trips/hooks/useTripChecklist'
import type { RangerAddedMeals } from '@/shared/api/ranger'
import { cn } from '@/shared/utils'

const DISMISSED_KEY_PREFIX = 'sitrepp.trips.nextStepsDismissed.'

/** The order people plan a trip in, which isn't the order Trip Readiness lists them. */
const STEP_ORDER: ChecklistItemId[] = ['details', 'itinerary', 'crew', 'meals', 'gear', 'radio']

interface StepCopy {
  title: string
  body: string
  action: string
}

function stepCopy(id: ChecklistItemId, trip: TripChecklistTrip): StepCopy {
  switch (id) {
    case 'details':
      return trip.start_date
        ? { title: 'Add a description', body: 'Tell the crew what this trip is about.', action: 'Edit details' }
        : { title: 'Pick your dates', body: 'Dates let the crew plan around it and put meals on the right days.', action: 'Set dates' }
    case 'itinerary':
      return { title: 'Add your route', body: 'Drop your camps and stops so everyone knows where they are headed.', action: 'Go to itinerary' }
    case 'crew':
      return { title: 'Invite your crew', body: 'Share a code or pick from your friends.', action: 'Invite' }
    case 'meals':
      return { title: 'Plan the meals', body: 'Add what everyone is eating.', action: 'Add a meal' }
    case 'gear':
      return { title: 'Build the gear list', body: 'List what the group needs so people can claim it.', action: 'Add gear' }
    case 'radio':
      return { title: 'Pick a radio channel', body: 'So everyone is on the same channel on the trail.', action: 'Set channel' }
  }
}

function joinNames(names: string[]): string {
  if (names.length < 2) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

function describeAddedMeals({ added, already_planned }: RangerAddedMeals): string {
  if (added.length === 0) return 'Those are already on the meal plan.'
  const addedText = `Added ${joinNames(added.map((meal) => meal.name))} to the meal plan.`
  return already_planned.length > 0 ? `${addedText} ${joinNames(already_planned)} was already there.` : addedText
}

function readDismissed(tripId: string): boolean {
  try {
    return window.localStorage.getItem(`${DISMISSED_KEY_PREFIX}${tripId}`) === 'true'
  } catch {
    return false
  }
}

function saveDismissed(tripId: string) {
  try {
    window.localStorage.setItem(`${DISMISSED_KEY_PREFIX}${tripId}`, 'true')
  } catch {
    // Storage blocked: the card just comes back next visit.
  }
}

interface MealsWithRangerProps {
  tripId: string
  hasDates: boolean
  onAdded: (message: string) => void
  onAddManually: () => void
}

function MealsWithRanger({ tripId, hasDates, onAdded, onAddManually }: MealsWithRangerProps) {
  const [foods, setFoods] = useState('')
  const addMeals = useAddMealsWithRanger(tripId)

  const submit = async () => {
    if (!foods.trim() || addMeals.isPending) return
    try {
      // Not mutate's onSuccess: the first meal completes this step, which unmounts this form,
      // and React Query drops a mutate callback once its component is gone.
      const result = await addMeals.mutateAsync(foods.trim())
      onAdded(describeAddedMeals(result))
    } catch {
      // Shown from addMeals.error, with the list still there to retry.
    }
  }

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <label htmlFor={`next-steps-foods-${tripId}`} className="text-sm text-earth-600 dark:text-earth-400">
        Name the food and Ranger adds the meals, with ingredients, on the right days.
      </label>
      <Textarea
        id={`next-steps-foods-${tripId}`}
        rows={2}
        value={foods}
        onChange={(event) => setFoods(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            submit()
          }
        }}
        placeholder="Pancakes, tacos, chili, s'mores"
        maxLength={1000}
        disabled={addMeals.isPending}
        className="placeholder:text-earth-500"
      />
      {!hasDates && (
        <p className="text-xs text-earth-600 dark:text-earth-400">
          No dates yet, so these go on days once you set them.
        </p>
      )}
      {addMeals.isError && (
        <p role="alert" className="text-sm text-destructive">
          {addMealsErrorMessage(addMeals.error)}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" disabled={!foods.trim() || addMeals.isPending} className="gap-1.5">
          <RangerMark thinking={addMeals.isPending} className="h-4 w-4" />
          {addMeals.isPending ? 'Adding meals...' : 'Add with Ranger'}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onAddManually} disabled={addMeals.isPending}>
          Add one myself
        </Button>
      </div>
    </form>
  )
}

interface TripNextStepsProps {
  tripId: string
  trip: TripChecklistTrip
  waypointCount: number
  memberCount: number
  canUseRanger: boolean
  /** False while the trip's waypoints are still loading, so finished steps don't flash up as to-dos. */
  isReady: boolean
  onStepAction: (id: ChecklistItemId) => void
  className?: string
}

/**
 * What to do next on a trip, however it was created. Lists only what's left, opens the first
 * one, and lets the user fill it in from here. Render with key={tripId}: its state is per trip.
 */
export function TripNextSteps({
  tripId,
  trip,
  waypointCount,
  memberCount,
  canUseRanger,
  isReady,
  onStepAction,
  className
}: TripNextStepsProps) {
  const { checklist, isLoading } = useTripChecklist(tripId, trip, { waypointCount, memberCount })
  const [dismissed, setDismissed] = useState(() => readDismissed(tripId))
  const [chosenStep, setChosenStep] = useState<ChecklistItemId | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  if (dismissed || isLoading || !isReady) return null

  const steps = STEP_ORDER.map((id) => checklist.find((item) => item.id === id)).filter(
    (item): item is NonNullable<typeof item> => Boolean(item) && !item?.completed
  )
  if (steps.length === 0 && !notice) return null

  const openStep = steps.some((step) => step.id === chosenStep) ? chosenStep : steps[0]?.id
  const doneCount = checklist.length - steps.length

  return (
    <Card aria-labelledby="trip-next-steps-title" role="region" className={cn('mb-8 overflow-hidden', className)}>
      <div className="flex items-center gap-3 px-6 pt-5">
        <h2 id="trip-next-steps-title" className="flex-1 text-lg font-semibold text-earth-900 dark:text-foreground">
          {steps.length > 0 ? 'Next up' : 'All set'}
        </h2>
        <span className="text-xs tabular-nums text-earth-600 dark:text-earth-400">
          {doneCount} of {checklist.length} done
        </span>
        <button
          type="button"
          aria-label="Hide next steps for this trip"
          title="Hide"
          onClick={() => {
            saveDismissed(tripId)
            setDismissed(true)
          }}
          className="rounded-md p-1 text-earth-500 transition-colors hover:bg-earth-100 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {notice && (
        <p role="status" className="mx-6 mt-3 flex items-start gap-2 text-sm text-earth-900 dark:text-foreground">
          <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-success" aria-hidden />
          <span>
            {notice}{' '}
            <button
              type="button"
              onClick={() => document.getElementById('meal-plan-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="font-medium text-adventure-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 dark:text-adventure-400"
            >
              See meal plan
            </button>
          </span>
        </p>
      )}

      <ul className="mt-3 divide-y divide-earth-200 border-t border-earth-200 dark:divide-earth-300 dark:border-earth-300">
        {steps.map((step) => {
          const copy = stepCopy(step.id, trip)
          const isOpen = step.id === openStep
          const panelId = `next-step-${step.id}`
          const rangerCanHelp = step.id === 'meals' && canUseRanger

          return (
            <li key={step.id}>
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setChosenStep(step.id)}
                className="flex w-full items-center gap-3 px-6 py-3 text-left transition-colors hover:bg-earth-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-adventure-500 dark:hover:bg-earth-100"
              >
                <span className="inline-block h-4 w-4 flex-shrink-0 rounded-full border-2 border-earth-300 dark:border-earth-600" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{copy.title}</span>
                {rangerCanHelp && !isOpen && (
                  <span className="flex flex-shrink-0 items-center gap-1 text-xs text-earth-600 dark:text-earth-400">
                    <RangerMark className="h-4 w-4 text-adventure-600" />
                    Ranger can do this
                  </span>
                )}
              </button>

              {isOpen && (
                <div id={panelId} className="px-6 pb-4 pl-[3.25rem]">
                  {rangerCanHelp ? (
                    <MealsWithRanger
                      tripId={tripId}
                      hasDates={Boolean(trip.start_date)}
                      onAdded={setNotice}
                      onAddManually={() => onStepAction(step.id)}
                    />
                  ) : (
                    <div className="flex flex-col items-start gap-2">
                      <p className="text-sm text-earth-600 dark:text-earth-400">{copy.body}</p>
                      <Button variant="outline" size="sm" onClick={() => onStepAction(step.id)} className="gap-1.5">
                        {copy.action}
                        <ArrowRight className="h-4 w-4" aria-hidden />
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </Card>
  )
}
